import { Alert } from '@customink/pigment-react';
import {
  useProduct,
  useProducts,
  useWCIBMO,
} from '@customink/project-service-react-components';
import type {
  DecoratedDesignResponse,
  ProjectItem as ProjectItemType,
} from '@customink/project-service-react-components';

import { GroupValidationResult, validateProjectItemGroup } from './validators';
import MinHighColorQty from '../Alerts/MinHighColorQty';
import MinimumColorQty from '../Alerts/MinimumColorQty';
import MinimumOrderQty from '../Alerts/MinimumOrderQty';
import MinimumSleeveQuantity from '../Alerts/MinimumSleeveQuantity';
import MinimumStyleQty from '../Alerts/MinimumStyleQty';
import NoQuantitiesEntered from '../Alerts/NoQuantitiesEntered';
import PricingError from '../Alerts/PricingError';
import QuantityNeededForPrice from '../Alerts/QuantityNeededForPrice';
import TooManyColorsError from '../Alerts/TooManyColorsError';
import { useZipCodeValid } from '../ProjectItemList/hooks';

import { WCIBMO_NAMES } from '@/constants';

type GroupErrorsProps = {
  projectItems: ProjectItemType[];
  design: DecoratedDesignResponse;
  handleEditGroupSizes?: () => void;
};

export default function GroupErrors({
  projectItems,
  design,
  handleEditGroupSizes,
}: GroupErrorsProps) {
  const colorIds: number[] = projectItems
    .map((item) => item.product?.colorId)
    .filter((id) => 'undefined' !== typeof id) as number[];
  const { data: products } = useProducts({ colorIds });

  const firstItemWithUnmetMinimum = projectItems?.find((projectItem) => {
    const colorId = projectItem.product?.colorId;
    if (!products || !colorId) return false;

    return products.find((product) =>
      product.colors.find(
        (color) => color.id === colorId && !color.singles_enabled,
      ),
    );
  });

  const colorId = firstItemWithUnmetMinimum?.product?.colorId;
  const { data: product } = useProduct({ colorId });

  const { message, wcibmo } = useWCIBMO(design?.compositeId);

  const groupValidationResult = validateProjectItemGroup({
    projectItems,
    design,
    product,
  });

  const { zipCodeValid } = useZipCodeValid();

  // We don't want to show group errors until the zip code is set and valid
  if (!zipCodeValid) {
    return null;
  }

  if (groupValidationResult === GroupValidationResult.ENTER_QUANTITIES) {
    return (
      <NoQuantitiesEntered onEnterQuantitiesClick={handleEditGroupSizes} />
    );
  }

  if (groupValidationResult === GroupValidationResult.TOO_MANY_COLORS) {
    return <TooManyColorsError design={design} product={product} />;
  }

  if (
    wcibmo === WCIBMO_NAMES.RAISE_ABOVE_HIGH_COLOR_MIN_OR_STYLE ||
    wcibmo === WCIBMO_NAMES.RAISE_ABOVE_HIGH_COLOR_MIN ||
    wcibmo === WCIBMO_NAMES.RAISE_ABOVE_HIGH_COLOR_MIN_OR_PRODUCT
  ) {
    return (
      <MinHighColorQty designCid={design?.compositeId} product={product} />
    );
  }

  if (wcibmo === WCIBMO_NAMES.RAISE_ABOVE_MIN_OR_PRODUCT) {
    return (
      <MinimumColorQty
        onIncreaseQuantityClick={handleEditGroupSizes}
        product={product}
        projectItem={firstItemWithUnmetMinimum}
      />
    );
  }

  if (wcibmo === WCIBMO_NAMES.RAISE_ABOVE_MIN_OR_STYLE) {
    return (
      <MinimumStyleQty
        onIncreaseQuantityClick={handleEditGroupSizes}
        product={product}
        projectItem={firstItemWithUnmetMinimum}
      />
    );
  }

  if (wcibmo === WCIBMO_NAMES.RAISE_ABOVE_MIN) {
    return (
      <MinimumOrderQty
        onIncreaseQuantityClick={handleEditGroupSizes}
        product={product}
        projectItem={firstItemWithUnmetMinimum}
      />
    );
  }

  if (wcibmo === WCIBMO_NAMES.RAISE_ABOVE_MIN_OR_REMOVE_SLEEVE) {
    return (
      <MinimumSleeveQuantity
        designCid={design?.compositeId}
        onEnterQuantitiesClick={handleEditGroupSizes}
      />
    );
  }

  if (wcibmo === 'network_error') {
    return <PricingError />;
  }

  if (wcibmo && message) {
    return <Alert severity="error">{message}</Alert>;
  }

  if (
    groupValidationResult ===
    GroupValidationResult.PROJECT_ITEM_MISSING_QUANTITIES
  ) {
    return <QuantityNeededForPrice />;
  }

  return null;
}
